// ================================================================================
// ECOMSIMPLY - BLUE-OCEAN RADAR™ INTERFACE COMPONENT
// Interface principale pour la détection d'opportunités Blue-Ocean
// ================================================================================

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Radar, Search, Zap, Target, TrendingUp, AlertCircle, 
  CheckCircle, Eye, ExternalLink, Download, BarChart3 
} from 'lucide-react';
import BlueOceanTable from './BlueOceanTable';
import BlueOceanDrawer from './BlueOceanDrawer';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

const BlueOceanInterface = ({
  filters,
  onFiltersChange,
  opportunities,
  loading,
  error,
  onScan,
  onLoadTop,
  onOpportunityClick
}) => {
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [scanPerformed, setScanPerformed] = useState(false);

  // Chargement automatique désactivé post-consolidation pour éviter polling agressif
  // L'utilisateur doit cliquer manuellement sur "Top Opportunités"
  /* 
  useEffect(() => {
    if (opportunities.length === 0 && !loading && !scanPerformed) {
      onLoadTop();
    }
  }, [opportunities.length, loading, scanPerformed, onLoadTop]);
  */

  const handleScanClick = () => {
    setScanPerformed(true);
    onScan();
  };

  const handleOpportunityClick = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowDrawer(true);
    if (onOpportunityClick) {
      onOpportunityClick(opportunity);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 55) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 85) return 'bg-green-100 text-green-700';
    if (score >= 70) return 'bg-blue-100 text-blue-700';
    if (score >= 55) return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Blue-Ocean Radar™ Header */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Radar className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-blue-900 mb-2">
                Blue-Ocean Radar™
              </h2>
              <p className="text-blue-700 mb-4">
                Détection automatisée d'opportunités produits à concurrence ultra-faible.
                Algorithmes propriétaires ESS, DGS, FMI, SRI + IA GPT-5 PRO.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-blue-100 text-blue-700">
                  🏆 Scoring Propriétaire
                </Badge>
                <Badge className="bg-green-100 text-green-700">
                  🧠 GPT-5 PRO Blueprint
                </Badge>
                <Badge className="bg-orange-100 text-orange-700">
                  🌊 Détection Blue-Ocean
                </Badge>
                <Badge className="bg-purple-100 text-purple-700">
                  ⚡ Feuille de Route Exploitable
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {opportunities.length}
            </div>
            <div className="text-sm text-blue-600">Opportunités</div>
          </div>
        </div>
      </Card>

      {/* Contrôles de scan */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700">
                Mots-clés de scan (seeds) :
              </label>
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                Séparés par espaces
              </Badge>
            </div>
            <input
              type="text"
              value={filters.query}
              onChange={(e) => onFiltersChange({ ...filters, query: e.target.value })}
              placeholder="kitchen gadgets smart fitness automotive..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lancez un scan avec 2-5 mots-clés pour explorer de nouveaux marchés
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={onLoadTop}
              disabled={loading}
              variant="outline"
              className="flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Top Opportunités
            </Button>
            
            <Button
              onClick={handleScanClick}
              disabled={loading || !filters.query?.trim()}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Zap className="w-4 h-4" />
              {loading ? 'Scan en cours...' : 'Lancer Scan'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Messages d'erreur */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <div className="flex items-center gap-3 text-red-800 p-4">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Statistiques rapides */}
      {opportunities.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {opportunities.filter(o => o.scores?.ess >= 85).length}
            </div>
            <div className="text-sm text-gray-600">ESS Excellent (≥85)</div>
            <div className="text-xs text-gray-500 mt-1">Étagères quasi-vides</div>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {opportunities.filter(o => o.scores?.dgs >= 80).length}
            </div>
            <div className="text-sm text-gray-600">DGS Excellent (≥80)</div>
            <div className="text-xs text-gray-500 mt-1">Pas de leader établi</div>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {opportunities.filter(o => o.scores?.fmi >= 90).length}
            </div>
            <div className="text-sm text-gray-600">FMI Excellent (≥90)</div>
            <div className="text-xs text-gray-500 mt-1">Premier entrant</div>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {opportunities.filter(o => o.scores?.bankability >= 85).length}
            </div>
            <div className="text-sm text-gray-600">Bankability ≥85</div>
            <div className="text-xs text-gray-500 mt-1">Prêts à exploiter</div>
          </Card>
        </div>
      )}

      {/* Tableau des opportunités ou message par défaut */}
      {opportunities.length > 0 ? (
        <BlueOceanTable
          opportunities={opportunities}
          loading={loading}
          onOpportunityClick={handleOpportunityClick}
        />
      ) : !loading && !scanPerformed ? (
        <Card className="p-8 text-center">
          <div className="text-blue-400 mb-4">
            <Radar className="w-16 h-16 mx-auto mb-4" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Blue-Ocean Radar™ prêt à scanner
          </h3>
          <p className="text-gray-600 mb-6">
            Détectez automatiquement des opportunités produits à concurrence ultra-faible.
            Lancez un scan avec vos mots-clés ou chargez le top des opportunités pré-calculées.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <Badge variant="secondary" className="text-xs">
              🎯 ESS: Empty-Shelf Score
            </Badge>
            <Badge variant="secondary" className="text-xs">
              📊 DGS: Dominance-Gap Score
            </Badge>
            <Badge variant="secondary" className="text-xs">
              🚀 FMI: First-Mover Index
            </Badge>
            <Badge variant="secondary" className="text-xs">
              🏭 SRI: Supplier Readiness
            </Badge>
            <Badge variant="secondary" className="text-xs">
              💎 Blueprint GPT-5
            </Badge>
          </div>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={onLoadTop}
              variant="outline"
              className="flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Voir Top Opportunités
            </Button>
            <Button
              onClick={() => onFiltersChange({ ...filters, query: 'smart home kitchen fitness' })}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Target className="w-4 h-4" />
              Exemple Scan
            </Button>
          </div>
        </Card>
      ) : loading ? (
        <Card className="p-8 text-center">
          <div className="animate-spin text-blue-600 mb-4">
            <Radar className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Scan Blue-Ocean en cours...
          </h3>
          <p className="text-gray-600">
            Analyse de marché, scoring propriétaire et génération de blueprints en cours.
            Cela peut prendre 30-60 secondes.
          </p>
        </Card>
      ) : (
        <Card className="p-8 text-center">
          <div className="text-gray-400 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune opportunité détectée
          </h3>
          <p className="text-gray-600 mb-4">
            Essayez d'autres mots-clés ou ajustez vos critères de recherche.
          </p>
          <Button
            onClick={onLoadTop}
            variant="outline"
            className="flex items-center gap-2 mx-auto"
          >
            <BarChart3 className="w-4 h-4" />
            Charger Top Opportunités
          </Button>
        </Card>
      )}

      {/* Drawer de détail Blue-Ocean */}
      <BlueOceanDrawer
        opportunity={selectedOpportunity}
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
      />
    </div>
  );
};

export default BlueOceanInterface;